import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { MsgEditor } from '../MsgEditor/MsgEditor';
import { RmButton, RmFooter } from '../RemindersEditor/EditorShell';
import { languageName } from '../../platform-languages';
import { hasUnknown, tokenPlain } from '../../msg/tokens';
import type { MsgMenuEntry, MsgToken } from '../../msg/tokens';
import {
  TC_ALWAYS_APPLIED,
  TC_AUDIENCE,
  TC_FIELDS,
  TC_READBACK,
  TC_STARTING_POINTS,
  tcReadbackFor,
} from '../../treatment/mockData';
import type { TcCampaignDraft, TcMsgChannel, TcFieldKey, TcStep } from '../../treatment/types';

export type TcBuilderPage = 'audience' | 'messages' | 'test';

export interface TiCampaignBuilderProps {
  page: TcBuilderPage;
  draft: TcCampaignDraft;
  onChange: (next: TcCampaignDraft) => void;
  onGoTo: (page: TcBuilderPage) => void;
  onCancel: () => void;
  onSaveDraft: () => void;
  onStart: () => void;
}

const STEPS: { id: TcBuilderPage; n: number; label: string }[] = [
  { id: 'audience', n: 1, label: 'Audience' },
  { id: 'messages', n: 2, label: 'Messages' },
  { id: 'test', n: 3, label: 'Test and start' },
];

/**
 * The TC campaign builder (amendment 27 §5) — **Recall's builder, three pages**,
 * with these differences: no goal cards (TC has one goal, book the treatment),
 * **text and email only** (no AI call, no human-call step), money conditions in
 * the audience, and every message must carry the **plan link** before it can be
 * saved. Prices, what a patient pays and x-rays are never in a message — they
 * live only on the plan page.
 */
export function TiCampaignBuilder({
  page,
  draft,
  onChange,
  onGoTo,
  onCancel,
  onSaveDraft,
  onStart,
}: TiCampaignBuilderProps) {
  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1180px] flex-1 overflow-y-auto px-8 pb-16 pt-6">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
        Treatment Intelligence › Campaigns › {draft.name || 'New campaign'}
      </div>
      <Stepper active={page} onGoTo={onGoTo} />

      {page === 'audience' ? (
        <AudiencePage draft={draft} onChange={onChange} onCancel={onCancel} onContinue={() => { onGoTo('messages'); }} />
      ) : page === 'messages' ? (
        <MessagesPage draft={draft} onChange={onChange} onBack={() => { onGoTo('audience'); }} onSaveDraft={onSaveDraft} onContinue={() => { onGoTo('test'); }} />
      ) : (
        <TestPage draft={draft} onBack={() => { onGoTo('messages'); }} onStart={onStart} />
      )}
    </div>
  );
}

function Stepper({ active, onGoTo }: { active: TcBuilderPage; onGoTo: (p: TcBuilderPage) => void }) {
  const activeIndex = STEPS.findIndex((s) => s.id === active);
  return (
    <>
      <h1 className="m-0 mt-1 text-[26px] font-bold leading-[1.15] tracking-[-0.025em] text-[var(--text-primary)]">
        {active === 'audience' ? (<>Who is this <span className="text-[var(--brand-indigo)]">for</span>?</>) : active === 'messages' ? (<>What do they <span className="text-[var(--brand-indigo)]">read</span>?</>) : (<>Check it, then <span className="text-[var(--brand-indigo)]">start</span></>)}
      </h1>
      <div className="mt-4 flex items-center gap-2.5">
        {STEPS.map((s, i) => {
          const done = i < activeIndex;
          const on = s.id === active;
          return (
            <React.Fragment key={s.id}>
              <button type="button" disabled={on} onClick={() => { onGoTo(s.id); }} className={cn('inline-flex items-center gap-1.5', !on && 'cursor-pointer hover:underline')}>
                <span className={cn('inline-flex h-[22px] w-[22px] items-center justify-center rounded-full font-[var(--font-mono)] text-[12.5px] font-bold', on || done ? 'bg-[var(--brand-indigo)] text-white' : 'bg-[#F1F3F9] text-[var(--text-muted)]')}>
                  {done ? <Icon kind="check" size={12} stroke={3} /> : s.n}
                </span>
                <span className={cn('text-[12.5px]', on ? 'font-bold text-[var(--text-primary)]' : 'text-[var(--text-muted)]')}>{s.label}</span>
              </button>
              {i < STEPS.length - 1 ? <span className="h-px w-8 bg-[var(--border-default)]" /> : null}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
}

/* ---- audience ------------------------------------------------------------ */

function AudiencePage({ draft, onChange, onCancel, onContinue }: { draft: TcCampaignDraft; onChange: (d: TcCampaignDraft) => void; onCancel: () => void; onContinue: () => void }) {
  const [built, setBuilt] = useState(draft.description.trim() !== '');
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);
  const r = tcReadbackFor(draft.description);
  const leftOut = draft.description.toLowerCase().includes('afford');
  // An Opportunity description (from a breakdown row) matches no starting card —
  // show it as a fifth, selected card (§3.1).
  const isStartingPoint = TC_STARTING_POINTS.some((sp) => sp.description === draft.description);
  const fromOpportunity = draft.description.trim() !== '' && !isStartingPoint;
  const willGet = Math.max(0, r.willGet - removed.size);

  return (
    <>
      <p className="m-0 mt-1.5 max-w-[720px] text-[13px] leading-[1.6] text-[var(--text-muted)]">
        Describe the patients and their treatment. ELVA reads it back so you can check.
      </p>

      {/* starting points — no goal cards */}
      <div className="mt-5">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Who should get it?</div>
        <p className="m-0 mt-1 text-[11.5px] text-[var(--text-muted)]">Pick a starting point or describe the patients and their treatment in your own words.</p>
        <div className="mt-2.5 flex flex-wrap gap-2">
          {fromOpportunity ? (
            <div className="w-[220px] rounded-[12px] border border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)] p-3 text-left">
              <span className="flex items-center justify-between">
                <span className="text-[12.5px] font-semibold text-[var(--text-primary)]">From Opportunity</span>
                <Icon kind="check" size={13} stroke={3} className="text-[var(--brand-indigo)]" />
              </span>
              <span className="mt-0.5 block text-[11px] text-[var(--text-muted)]">{draft.description}</span>
              <span className="mt-1 block font-[var(--font-mono)] text-[12px] font-bold text-[var(--text-primary)]">{r.match} patients</span>
            </div>
          ) : null}
          {TC_STARTING_POINTS.map((sp) => {
            const on = draft.description === sp.description;
            return (
              <button key={sp.id} type="button" onClick={() => { onChange({ ...draft, description: sp.description, name: draft.name || sp.name }); setBuilt(true); setRemoved(new Set()); }}
                className={cn('w-[220px] rounded-[12px] border p-3 text-left', on ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)]' : 'border-[var(--border-default)] bg-[var(--surface-card)] hover:border-[#D6DBEA]')}>
                <span className="flex items-center justify-between">
                  <span className="text-[12.5px] font-semibold text-[var(--text-primary)]">{sp.name}</span>
                  {on ? <Icon kind="check" size={13} stroke={3} className="text-[var(--brand-indigo)]" /> : null}
                </span>
                <span className="mt-0.5 block text-[11px] text-[var(--text-muted)]">{sp.sub}</span>
                <span className="mt-1 block font-[var(--font-mono)] text-[12px] font-bold text-[var(--text-primary)]">{sp.patients} patients</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* describe */}
      <div className="mt-4 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Describe the patients</div>
        <textarea value={draft.description} onChange={(e) => { onChange({ ...draft, description: e.target.value }); setBuilt(false); }} rows={2}
          className="mt-1.5 w-full rounded-[10px] border border-[#dfe4ee] px-3.5 py-2.5 text-[13.5px] leading-[1.6] text-[var(--text-primary)] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]" />
        <button type="button" onClick={() => { setBuilt(true); }} className="mt-2 rounded-[9px] bg-[var(--brand-indigo)] px-3 py-1.5 text-[12.5px] font-semibold text-white">Build the list</button>
        <div className="mt-1.5 text-[11px] leading-[1.55] text-[var(--text-muted)]">
          You can use money here: <b className="font-semibold text-[var(--text-secondary)]">case value</b>, <b className="font-semibold text-[var(--text-secondary)]">patient pays after insurance</b>, <b className="font-semibold text-[var(--text-secondary)]">benefits left</b>, <b className="font-semibold text-[var(--text-secondary)]">benefits reset</b>. Insurance figures come from ELVA’s insurance checks.
        </div>

        {built ? (
          <div className="mt-3 rounded-[10px] bg-[#E8F3EC] p-3">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--money-green)]">ELVA understood this as</div>
            <div className="mt-1.5 flex flex-col gap-1 text-[12px] text-[var(--text-secondary)]">
              {r.understood.map((u) => (<span key={u.label}><b className="font-semibold text-[var(--text-primary)]">{u.label}:</b> {u.value}</span>))}
            </div>
            {leftOut ? (
              <div className="mt-2 rounded-[8px] bg-[#FBF3E4] px-3 py-2 text-[11.5px] leading-[1.55] text-[var(--text-secondary)]">
                <b className="font-semibold text-[var(--warn-amber)]">Left out: “{TC_READBACK.leftOut.phrase}”.</b> {TC_READBACK.leftOut.reason}
              </div>
            ) : null}
            <div className="mt-2 border-t border-[color-mix(in_srgb,var(--money-green)_25%,transparent)] pt-2">
              <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">Always applied</div>
              <div className="mt-1 flex flex-col gap-1.5">
                {TC_ALWAYS_APPLIED.map((a) => (
                  <span key={a.label} className="flex items-start gap-1.5 text-[11.5px] text-[var(--text-secondary)]">
                    <Icon kind="lock" size={10} stroke={2.2} className="mt-0.5 shrink-0 text-[var(--text-muted)]" />
                    <span><b className="font-semibold text-[var(--text-primary)]">{a.label}</b> — {a.sub}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {built ? (
        <>
          <div className="mt-3 text-[13px] text-[var(--text-secondary)]">
            <b className="font-[var(--font-mono)] font-bold text-[var(--text-primary)]">{r.match}</b> match · {r.matchValue} of treatment →{' '}
            <b className="font-[var(--font-mono)] font-bold text-[var(--text-primary)]">{willGet}</b> will get this campaign
          </div>
          <div className="mt-1 text-[11.5px] leading-[1.55] text-[var(--text-muted)]">The other {r.match - willGet}: {r.theOther}</div>

          <div className="mt-3 overflow-hidden rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)]">
            <div className="grid grid-cols-[1.5fr_1.5fr_0.8fr_0.9fr_0.7fr_1fr] gap-x-3 border-b border-[var(--border-default)] px-4 py-2 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.07em] text-[var(--text-muted)]">
              <span>Patient · insurance</span><span>Treatment</span><span className="text-right">Case value</span><span className="text-right">Patient pays</span><span>Diagnosed</span><span />
            </div>
            {(showAll ? TC_AUDIENCE : TC_AUDIENCE.slice(0, 5)).map((p) => {
              const out = removed.has(p.id);
              return (
                <div key={p.id} className={cn('grid grid-cols-[1.5fr_1.5fr_0.8fr_0.9fr_0.7fr_1fr] items-center gap-x-3 border-b border-[var(--border-default)] px-4 py-2 text-[12px] last:border-0', out && 'opacity-45')}>
                  <span className="min-w-0"><span className="block truncate font-semibold text-[var(--text-primary)]">{p.name}</span><span className="block text-[11px] text-[var(--text-muted)]">{p.age} · {p.insurance}</span></span>
                  <span className="min-w-0 truncate text-[var(--text-secondary)]">{p.treatment}</span>
                  <span className="text-right font-[var(--font-mono)] text-[var(--text-secondary)]">{p.caseValue}</span>
                  <span className="text-right font-[var(--font-mono)] text-[var(--text-secondary)]">{p.patientPays}</span>
                  <span className="text-[var(--text-muted)]">{p.diagnosed}</span>
                  <span className="flex items-center justify-end gap-2">
                    {out ? (
                      <>
                        <span className="text-[11px] font-semibold text-[var(--text-muted)]">Left out</span>
                        <button type="button" onClick={() => { setRemoved((s) => { const n = new Set(s); n.delete(p.id); return n; }); }} className="text-[11px] font-semibold text-[var(--brand-indigo)] hover:underline">Undo</button>
                      </>
                    ) : (
                      <>
                        <span className="text-[11px] font-semibold" style={{ color: p.status === 'will-get-it' ? 'var(--money-green)' : 'var(--text-muted)' }}>● {p.statusLabel}</span>
                        <button type="button" onClick={() => { setRemoved((s) => new Set(s).add(p.id)); }} className="text-[11px] font-semibold text-[var(--brand-indigo)] hover:underline">Leave out</button>
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          <button type="button" onClick={() => { setShowAll((v) => !v); }} className="mt-2 text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">
            {showAll ? 'Show fewer' : `See all ${String(r.match)}`}
          </button>
          <div className="mt-1.5 text-[11px] text-[var(--text-muted)]">
            <b className="font-semibold text-[var(--text-secondary)]">Patient pays</b> comes from ELVA’s insurance check. A range means the check is more than 14 days old — it’s checked again when the patient opens their plan.
          </div>
        </>
      ) : null}

      <RmFooter left={<span>Saved as a draft as you go.</span>}>
        <RmButton onClick={onCancel}>Cancel</RmButton>
        <RmButton kind="primary" onClick={onContinue}>Continue to messages</RmButton>
      </RmFooter>
    </>
  );
}

/* ---- messages ------------------------------------------------------------ */

function MessagesPage({ draft, onChange, onBack, onSaveDraft, onContinue }: { draft: TcCampaignDraft; onChange: (d: TcCampaignDraft) => void; onBack: () => void; onSaveDraft: () => void; onContinue: () => void }) {
  const [selectedId, setSelectedId] = useState<string | undefined>(draft.steps[0]?.id);
  const selected = draft.steps.find((s) => s.id === selectedId) ?? draft.steps[0];
  const issues = tcIssues(draft.steps);

  const setSteps = (steps: TcStep[]) => { onChange({ ...draft, steps }); };
  const addStep = () => {
    const last = draft.steps[draft.steps.length - 1];
    const step: TcStep = { id: `s${String(Date.now())}`, channel: 'text', day: (last?.day ?? 0) + 7, mode: 'template', body: [] };
    setSteps([...draft.steps, step]);
    setSelectedId(step.id);
  };

  return (
    <>
      <p className="m-0 mt-1.5 max-w-[720px] text-[13px] leading-[1.6] text-[var(--text-muted)]">
        Each step is one text or email. Every one links to the patient’s plan page.
      </p>

      {/* top bar — name + who, no goal */}
      <div className="mt-4 flex flex-wrap items-end gap-6 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] px-5 py-4">
        <div className="min-w-[260px] flex-1">
          <label htmlFor="tc-name" className="block text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Name</label>
          <input id="tc-name" value={draft.name} placeholder="Untitled campaign" onChange={(e) => { onChange({ ...draft, name: e.target.value }); }}
            className="mt-1 w-full border-0 border-b border-[#dfe4ee] bg-transparent pb-1 text-[19px] font-bold tracking-[-0.02em] text-[var(--text-primary)] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]" />
        </div>
        <div className="shrink-0">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Who</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="font-[var(--font-mono)] text-[14px] font-bold text-[var(--text-primary)]">{TC_READBACK.willGet} patients · {TC_READBACK.willGetValue}</span>
            <button type="button" onClick={onBack} className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">Change →</button>
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-start gap-5 max-[1100px]:flex-col">
        {/* steps column */}
        <div className="w-[360px] shrink-0 max-[1100px]:w-full">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">The steps</div>
          <div className="mt-2.5 flex flex-col gap-2">
            {draft.steps.map((s) => (
              <button key={s.id} type="button" onClick={() => { setSelectedId(s.id); }}
                className={cn('rounded-[12px] border p-3 text-left', s.id === selected?.id ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)]' : 'border-[var(--border-default)] bg-[var(--surface-card)] hover:border-[#D6DBEA]')}>
                <span className="flex items-center gap-2">
                  <Icon kind={s.channel === 'email' ? 'mail' : 'chat'} size={12} stroke={2} className="text-[var(--text-muted)]" />
                  <span className="text-[12.5px] font-bold text-[var(--text-primary)]">{s.channel === 'email' ? 'Email' : 'Text'}</span>
                  <span className="font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">day {s.day}</span>
                  <span className="rounded-[5px] bg-[#F1F3F9] px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.06em] text-[var(--text-muted)]">{s.mode === 'smart' ? 'Smart' : 'Template'}</span>
                </span>
                <span className="mt-1 block truncate text-[11.5px] text-[var(--text-secondary)]">
                  {tokenPlain(s.body, (k) => TC_FIELDS.find((f) => f.key === k)?.label ?? k) || 'No message yet'}
                </span>
                {issues.some((x) => x.stepId === s.id) ? (
                  <span className="mt-1 flex items-center gap-1.5 text-[11px] font-semibold text-[var(--alert-red)]"><span className="h-[6px] w-[6px] rounded-full bg-[var(--alert-red)]" />{issues.find((x) => x.stepId === s.id)?.short}</span>
                ) : null}
              </button>
            ))}
          </div>
          <button type="button" onClick={addStep} className="mt-2 w-full rounded-[12px] border border-dashed border-[var(--brand-indigo)] px-3 py-2.5 text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:bg-[var(--brand-indigo-tint)]">
            + Add a text or email
          </button>
          <p className="m-0 mt-3 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
            Texts and emails only. Calls are made by your team, from the Call list.{' '}
            <b className="font-semibold text-[var(--text-secondary)]">A patient leaves</b> when they book, say no twice, opt out, or their treatment is done or changed.
          </p>
        </div>

        {/* editor */}
        <div className="min-w-0 flex-1">
          {selected === undefined ? null : (
            <TcStepEditor step={selected} onChange={(next) => { setSteps(draft.steps.map((s) => (s.id === next.id ? next : s))); }} onRemove={() => { const rest = draft.steps.filter((s) => s.id !== selected.id); setSteps(rest); setSelectedId(rest[0]?.id); }} />
          )}
        </div>
      </div>

      <RmFooter
        left={
          issues.length === 0 ? (
            <span className="text-[12.5px] font-semibold text-[var(--money-green)]">Every step is ready.</span>
          ) : (
            <span className="flex flex-col gap-1">
              <span className="text-[12.5px] font-semibold text-[var(--text-muted)]">{issues.length} thing{issues.length === 1 ? '' : 's'} to fix before you can start:</span>
              {issues.map((x, i) => (
                <button key={`${x.stepId}-${String(i)}`} type="button" onClick={() => { setSelectedId(x.stepId); }} className="text-left text-[12.5px] font-semibold text-[var(--alert-red)] hover:underline">{x.long} →</button>
              ))}
            </span>
          )
        }
      >
        <RmButton onClick={onBack}>Back</RmButton>
        <RmButton onClick={onSaveDraft}>Save as draft</RmButton>
        <RmButton kind="primary" disabled={issues.length > 0} title={issues.length > 0 ? 'Add the plan link to every step first' : undefined} onClick={onContinue}>Continue to test and start</RmButton>
      </RmFooter>
    </>
  );
}

function TcStepEditor({ step, onChange, onRemove }: { step: TcStep; onChange: (s: TcStep) => void; onRemove: () => void }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menu: MsgMenuEntry<TcFieldKey>[] = TC_FIELDS.map((f) => ({ field: f, unavailable: undefined }));
  const missingPlanLink = !bodyHasPlanLink(step.body);

  return (
    <div className="rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[14px] font-bold text-[var(--text-primary)]">
            {step.channel === 'email' ? 'Email' : 'Text'} · day {step.day}
          </div>
          <div className="mt-0.5 text-[11.5px] text-[var(--text-muted)]">{step.day === 0 ? 'The day a patient joins this campaign' : `${String(step.day)} days after a patient joins`}</div>
        </div>
        <button type="button" onClick={onRemove} className="text-[12px] font-semibold text-[var(--alert-red)] hover:underline">Remove step</button>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-6">
        <Labelled label="Channel">
          {/* Text · Email only — no dropdown, no AI call, no human call. */}
          <span className="inline-flex gap-1 rounded-[9px] bg-[var(--surface-sunken)] p-[3px]">
            {(['text', 'email'] as TcMsgChannel[]).map((c) => (
              <button key={c} type="button" onClick={() => { onChange({ ...step, channel: c }); }}
                className={cn('rounded-[7px] px-2.5 py-1 text-[12px]', c === step.channel ? 'bg-[var(--surface-card)] font-semibold text-[var(--text-primary)] shadow-[0_1px_2px_rgba(36,41,54,.08)]' : 'text-[var(--text-secondary)]')}>
                {c === 'email' ? 'Email' : 'Text'}
              </button>
            ))}
          </span>
        </Labelled>
        <Labelled label="Send on day">
          <input type="number" min={0} value={step.day} onChange={(e) => { onChange({ ...step, day: Math.max(0, Number(e.target.value)) }); }}
            className="h-8 w-[64px] rounded-[9px] border border-[#dfe4ee] px-2 text-center font-[var(--font-mono)] text-[13px] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]" />
        </Labelled>
        <Labelled label="Who writes it">
          <span className="inline-flex gap-1 rounded-[9px] bg-[var(--surface-sunken)] p-[3px]">
            {(['template', 'smart'] as const).map((m) => (
              <button key={m} type="button" onClick={() => { onChange({ ...step, mode: m }); }}
                className={cn('rounded-[7px] px-2.5 py-1 text-[12px]', m === step.mode ? 'bg-[var(--surface-card)] font-semibold text-[var(--text-primary)] shadow-[0_1px_2px_rgba(36,41,54,.08)]' : 'text-[var(--text-secondary)]')}>
                {m === 'smart' ? 'Smart' : 'Template'}
              </button>
            ))}
          </span>
        </Labelled>
      </div>

      <div className="mt-3 text-[11.5px] text-[var(--text-muted)]">Sent in each patient’s language — Ruth gets Spanish.</div>

      <div className="mt-3">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Message</div>
        <div className="mt-1.5">
          <MsgEditor<TcFieldKey>
            value={step.body}
            onChange={(body) => { onChange({ ...step, body }); }}
            menu={menu}
            rows={3}
            ariaLabel="Message"
            placeholder="What every patient gets, with their own details filled in"
            onMenuOpenChange={setMenuOpen}
          />
        </div>
        {/* The locked note — never a price, what they pay, or an x-ray in a message (§1). */}
        <div className="mt-1.5 rounded-[9px] bg-[var(--surface-sunken)] px-3 py-2 text-[11px] leading-[1.55] text-[var(--text-muted)]">
          <b className="font-semibold text-[var(--text-secondary)]">Prices, what they pay, x-rays</b> — only on the plan page, after the patient enters their date of birth. Never in a text or email.
        </div>
        {/* Plan-link required (§5.2). */}
        {missingPlanLink ? (
          <div className="mt-1.5 rounded-[9px] bg-[#FBF3E4] px-3 py-2 text-[11.5px] font-semibold text-[var(--warn-amber)]">
            Add <b>Plan link</b> — every message needs it before the step can be saved.
          </div>
        ) : null}
        {/* Character count (§4.2) — text steps only. */}
        {step.channel === 'text' ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 text-[11px] text-[var(--text-muted)]">
            <span className="font-[var(--font-mono)]">148 characters · 1 text message</span>
            <span>·</span>
            <span>“Reply STOP to opt out” is added for you</span>
          </div>
        ) : null}
      </div>

      {/* Send this step as a test (§4.2). */}
      <StepTestRow />

      {/* how it reads */}
      <div className="mt-4">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">How it reads</div>
        <div className="mt-2 grid grid-cols-3 gap-2.5 max-[1100px]:grid-cols-1">
          {TC_AUDIENCE.slice(0, 3).map((p) => (
            <div key={p.id} className="rounded-[10px] border border-[var(--border-default)] bg-[#FAFBFE] p-3">
              <div className="text-[11.5px] font-semibold text-[var(--text-primary)]">
                {p.name}{p.language !== 'en' ? ` · ${languageName(p.language)}` : ''}
              </div>
              <div className="mt-1 text-[11.5px] leading-[1.6] text-[var(--text-secondary)]">
                {renderPreview(step, p)}
                {menuOpen ? <span className="ml-1 rounded-[5px] bg-[var(--brand-indigo-tint)] px-1.5 py-0.5 text-[10.5px] font-semibold text-[var(--brand-indigo)]">choosing a field…</span> : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (<div><div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">{label}</div><div className="mt-1.5">{children}</div></div>);
}

/** Send this step as a test (§4.2) — a phone field and visible feedback. */
function StepTestRow() {
  const [phone, setPhone] = useState('(210) 555-0100');
  const [sent, setSent] = useState(false);
  return (
    <div className="mt-4 rounded-[12px] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-3">
      <div className="text-[11.5px] font-semibold text-[var(--text-primary)]">Send this step as a test</div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input value={phone} onChange={(e) => { setPhone(e.target.value); setSent(false); }} className="h-8 w-[160px] rounded-[9px] border border-[#dfe4ee] bg-[var(--surface-card)] px-2.5 text-[12.5px] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]" />
        <RmButton kind="soft" onClick={() => { setSent(true); }}>Send test</RmButton>
        {sent ? <span className="text-[11.5px] font-semibold text-[var(--money-green)]">Sent to {phone}</span> : null}
      </div>
    </div>
  );
}

function bodyHasPlanLink(body: MsgToken<TcFieldKey>[]): boolean {
  return body.some((t) => t.kind === 'field' && t.field === 'plan_link');
}

interface TcIssue { stepId: string; short: string; long: string }
function tcIssues(steps: TcStep[]): TcIssue[] {
  const out: TcIssue[] = [];
  steps.forEach((s, i) => {
    if (!bodyHasPlanLink(s.body)) out.push({ stepId: s.id, short: 'Needs the plan link', long: `Step ${String(i + 1)} needs the plan link` });
    else if (hasUnknown(s.body, s.subject)) out.push({ stepId: s.id, short: 'Unknown field', long: `Step ${String(i + 1)} has braces that match no field` });
  });
  return out;
}

function renderPreview(step: TcStep, p: { name: string; treatmentShort?: string; treatment: string; diagnosed: string; language: string }): string {
  const first = p.name.split(' ')[0] ?? '';
  const treatment = p.treatmentShort ?? 'your treatment';
  const words = tokenPlain(step.body, (k) => {
    if (k === 'plan_link') return 'plan link';
    if (k === 'first_name') return first;
    if (k === 'treatment') return treatment;
    if (k === 'diagnosed') return `on ${p.diagnosed}`;
    return TC_FIELDS.find((f) => f.key === k)?.example ?? k;
  });
  if (words.trim() === '') return 'Add a message to preview it.';
  // Ruth reads Spanish — the message goes out in her language (fix 29 §4.2).
  if (p.language === 'es') {
    return `Hola ${first}, ${p.treatmentShort === 'a crown' ? 'su corona' : 'su tratamiento'} del ${p.diagnosed}. Vea su plan y lo que cubre su seguro aquí: plan link`;
  }
  return words;
}

/* ---- test ---------------------------------------------------------------- */

function TestPage({ draft, onBack, onStart }: { draft: TcCampaignDraft; onBack: () => void; onStart: () => void }) {
  const [to, setTo] = useState('(210) 555-0100');
  const [sent, setSent] = useState<string | null>(null);
  const sample = TC_AUDIENCE[0];
  return (
    <>
      <p className="m-0 mt-1.5 max-w-[720px] text-[13px] leading-[1.6] text-[var(--text-muted)]">
        See each step the way a patient will, send it to yourself, and start when it’s right.
      </p>

      {/* each step's preview (§4.3) */}
      <div className="mt-5 flex flex-col gap-2.5">
        {draft.steps.map((s, i) => (
          <div key={s.id} className="rounded-[12px] border border-[var(--border-default)] bg-[#FAFBFE] p-3.5">
            <div className="flex items-center gap-2">
              <span className="font-[var(--font-mono)] text-[11px] font-bold text-[var(--text-muted)]">Step {i + 1}</span>
              <Icon kind={s.channel === 'email' ? 'mail' : 'chat'} size={12} stroke={2} className="text-[var(--text-muted)]" />
              <span className="text-[12px] font-bold text-[var(--text-primary)]">{s.channel === 'email' ? 'Email' : 'Text'}</span>
              <span className="font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">{s.day === 0 ? 'day 0' : `day ${String(s.day)}`}</span>
            </div>
            <p className="m-0 mt-1.5 text-[12px] leading-[1.6] text-[var(--text-secondary)]">
              {sample !== undefined ? renderPreview(s, sample) : 'Add a message to preview it.'}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4">
        <div className="text-[12.5px] font-bold text-[var(--text-primary)]">Send this campaign to yourself</div>
        <p className="m-0 mt-1 text-[11.5px] text-[var(--text-muted)]">A made-up patient. The plan link opens a sample plan — date of birth 01/01/1980.</p>
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <input value={to} onChange={(e) => { setTo(e.target.value); setSent(null); }} className="h-8 w-[180px] rounded-[9px] border border-[#dfe4ee] px-2.5 text-[12.5px] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]" />
          <RmButton onClick={() => { setSent('first'); }}>Send the first step</RmButton>
          <RmButton kind="soft" onClick={() => { setSent('all'); }}>Send every step</RmButton>
          {sent !== null ? <span className="text-[11.5px] font-semibold text-[var(--money-green)]">Sent to {to}</span> : null}
        </div>
      </div>
      <div className="mt-4 rounded-[16px] border border-[var(--money-green)] bg-[var(--surface-card)] px-5 py-4" style={{ borderTopWidth: 3 }}>
        <div className="flex items-center gap-1.5 text-[13px] font-bold text-[var(--money-green)]"><Icon kind="check" size={14} stroke={3} />Every step is ready</div>
        <div className="mt-2 text-[12.5px] text-[var(--text-secondary)]">{TC_READBACK.willGet} patients · first texts go out tomorrow at 9:00 am.</div>
      </div>
      <RmFooter>
        <RmButton onClick={onBack}>Back</RmButton>
        <RmButton kind="primary" onClick={onStart}>Start the campaign</RmButton>
      </RmFooter>
    </>
  );
}
